<template>
  <div>
    <a-card size="small" :loading="loading">
      <a-page-header :title="data.name" :sub-title="`任务详情 #TS-${taskId}`">
        <template slot="extra">
          <a-button key="2" v-tc="{ name: '关联契约' }" type="primary" @click="connectApi"> 关联契约 </a-button>
          <ai-score-view key="3" v-tc="{ name: 'AI打分' }" :scoring="data.scoring" :ai-score="data.ai_score" @trigger="aiScoreTrigger" @scoring="getTaskDetail" />
          <a-dropdown placement="bottomRight">
            <a-menu slot="overlay">
              <a-menu-item key="1">
                <a v-tc="{ name: '编辑任务' }" @click="onEdit"><a-icon type="edit" /> 编辑任务</a>
              </a-menu-item>
              <a-menu-item key="2">
                <a v-tc="{ name: '修改迭代' }" @click="onRelationVersion"><a-icon type="form" /> 修改迭代</a>
              </a-menu-item>
              <a-menu-item key="3">
                <a v-tc="{ name: '提审任务' }" @click="onCreateWorkflow"><a-icon type="bell" /> 提审任务</a>
              </a-menu-item>
              <a-menu-item key="4">
                <a v-tc="{ name: '移动项目' }" @click="onMoveProject"><a-icon type="export" /> 移动项目</a>
              </a-menu-item>
              <a-menu-item key="5">
                <a v-tc="{ name: '删除任务' }" @click="onDelete"><a-icon type="delete" /> 删除任务</a>
              </a-menu-item>
              <a-menu-item key="6">
                <a v-tc="{ name: '复制任务ID' }" @click="onTextCopy(`TS-${taskId}`)"><a-icon type="copy" /> 复制任务ID</a>
              </a-menu-item>
              <a-menu-item key="7">
                <a v-tc="{ name: '复制Commit信息' }" @click="onTextCopy(`#TS-${taskId}@${data.name}`)"><a-icon type="copy" /> 复制Commit信息</a>
              </a-menu-item>
            </a-menu>
            <a-button style="margin-left: 8px"> 更多 <a-icon type="down" /> </a-button>
          </a-dropdown>
        </template>
        <template slot="tags">
          <a-tag v-if="isDeleted" color="red"> 已删除 </a-tag>
        </template>
        <a-descriptions size="small" :column="3">
          <a-descriptions-item label="状态">
            <task-status-edit :disabled="isDeleted" :task-id="taskId" :next-status="data.next_status" :status="data.status" @change="getTaskDetail" />
          </a-descriptions-item>
          <a-descriptions-item label="标签">
            <template slot="content">
              <a-tag v-for="(tag, index) in filteredTags(data.tags)" :key="index" :color="tag.color">{{ tag.name }}</a-tag>
            </template>
            <template slot="title">
              <span>标签</span>
            </template>
            <a-tag v-for="(tag, index) in filteredTags(data.tags)" :key="index" :color="tag.color">{{ tag.name }}</a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="优先级">
            <task-priority-edit :disabled="isDeleted" :task-id="taskId" :value="data.priority" @change="getTaskDetail" />
          </a-descriptions-item>
          <a-descriptions-item label="处理人">
            <task-user-edit :disabled="isDeleted" style="width: 180px" :source-id="taskId" :value="data.handlers" @change="getTaskDetail" />
          </a-descriptions-item>
          <a-descriptions-item label="计划开始时间">
            <task-date-edit :disabled="isDeleted" :data-source="data" item-name="pre_start_time" @change="getTaskDetail" />
          </a-descriptions-item>
          <a-descriptions-item label="计划结束时间">
            <task-date-edit :disabled="isDeleted" :data-source="data" item-name="pre_end_time" @change="getTaskDetail" />
          </a-descriptions-item>
          <a-descriptions-item label="完成时间"> {{ data.completed_time || '--' }}</a-descriptions-item>
          <a-descriptions-item label="创建人">
            <user-profiles :value="data.creator ? [data.creator.id] : []" avatar />
          </a-descriptions-item>
          <a-descriptions-item label="创建时间">{{ data.created_time }} </a-descriptions-item>
          <a-descriptions-item label="更新时间"> {{ data.updated_time }}</a-descriptions-item>
          <a-descriptions-item label="任务点数">
            <task-point-edit :disabled="isDeleted" :value="data.point" :task-id="data.id" @change="getTaskDetail" />
          </a-descriptions-item>
          <a-descriptions-item label="任务类型">
            <span v-if="data.objective.length > 0">
              <a-tag v-for="(item, index) in data.objective" :key="index" :color="item.color">{{ item.name }}</a-tag>
            </span>
            <span v-else>--</span>
          </a-descriptions-item>
          <a-descriptions-item label="关联迭代">
            <a v-if="Object.keys(data.version).length > 0" class="story_title" :href="versionLink(data)" target="_blank">{{ data.version.name }}</a>
            <span v-else>--</span>
          </a-descriptions-item>
          <a-descriptions-item label="关联需求">
            <a v-if="data.requirement && data.requirement.id !== null" class="story_title" :href="link(data)" target="_blank">{{ data.requirement.name }}</a>
            <span v-else>--</span>
          </a-descriptions-item>
          <a-descriptions-item label="后补原因">
            <span v-if="Array.isArray(data.supplement_reasons) && data.supplement_reasons.length > 0">
              <a-tag v-for="(item, index) in data.supplement_reasons" :key="index" :color="item.color">{{ item.name }}</a-tag>
            </span>
            <span v-else>--</span>
          </a-descriptions-item>
        </a-descriptions>
        <a-tabs v-model="tabsCurrent" size="small" @change="handleTabsChange">
          <a-tab-pane key="1" tab="详细信息">
            <am-editor :content="data.description" model="view" :drag="false" />
          </a-tab-pane>
          <a-tab-pane v-if="data.workflows && data.workflows.length > 0" key="2" tab="关联流程">
            <workflow-data-list :table-data="data.workflows" :pagination="false" category="task" />
          </a-tab-pane>
          <a-tab-pane v-if="data.releases && data.releases.length > 0" key="3" tab="发布评审">
            <workflow-release :table-data="data.releases" />
          </a-tab-pane>
          <a-tab-pane key="4" tab="关联缺陷">
            <story-issue-list :issue-list="issueList" :loading="loading" />
          </a-tab-pane>
          <a-tab-pane key="5" tab="变更历史">
            <task-changelog ref="changelog" :source-id="taskId" />
          </a-tab-pane>
          <a-tab-pane key="6" tab="相关代码">
            <task-code :source-id="taskId" />
          </a-tab-pane>
          <a-tab-pane key="7" tab="相关契约" force-render>
            <ApiVersionView ref="apiVersionViewRef" type="task" @deleteOk="getTaskDetail" />
          </a-tab-pane>
        </a-tabs>
      </a-page-header>
      <comment-panel ref="commentRef" mode="task" :source-id="taskId" />
    </a-card>
    <a-back-top />
    <task-edit ref="taskEditRef" @close="close" @cancel="onCancel" />
    <task-project-edit ref="taskProjectEditRef" @ok="onMoveOk" />
    <task-delete-confirm ref="taskDeleteRef" @ok="close" />
    <task-version-edit ref="taskVersionRef" @ok="close" />
    <ApiConnect ref="apiConnectRef" :task-id="taskId" @close="close" @submit="getTaskDetail" />
  </div>
</template>

<script>
import { tagList } from '@/api/system/tag';
import { issueList } from '@/api/project/issue';
import { onCopy as onTextCopy } from '@/utils/utils';
import { taskDetail, aiScore } from '@/api/project/task';
import { workflowFastCreate } from '@/api/project/workflow';

import AmEditor from '@/components/AmEditor/index';
import CommentPanel from '@/components/comment/CommentPanel';
import TaskEdit from '@/pages/project/task/components/TaskEdit';
import TaskCode from '@/pages/project/task/components/TaskCode';
import ApiConnect from '@/pages/project/task/components/ApiConnect';
import AiScoreView from '@/pages/project/task/components/AiScoreView';
import TaskDateEdit from '@/pages/project/task/components/TaskDateEdit';
import TaskUserEdit from '@/pages/project/task/components/TaskUserEdit';
import TaskPointEdit from '@/pages/project/task/components/TaskPointEdit';
import TaskChangelog from '@/pages/project/task/components/TaskChangelog';
import ApiVersionView from '@/pages/project/task/components/ApiVersionView';
import TaskStatusEdit from '@/pages/project/task/components/TaskStatusEdit';
import StoryIssueList from '@/pages/project/story/components/StoryIssueList';
import TaskProjectEdit from '@/pages/project/task/components/TaskProjectEdit';
import TaskVersionEdit from '@/pages/project/task/components/TaskVersionEdit';
import WorkflowRelease from '@/pages/project/story/components/WorkflowRelease';
import TaskPriorityEdit from '@/pages/project/task/components/TaskPriorityEdit';
import TaskDeleteConfirm from '@/pages/project/task/components/TaskDeleteConfirm';
import WorkflowDataList from '@/pages/project/workflow/components/WorkflowDataList';

export default {
  components: {
    AmEditor,
    TaskEdit,
    TaskCode,
    ApiConnect,
    AiScoreView,
    TaskDateEdit,
    TaskUserEdit,
    CommentPanel,
    TaskPointEdit,
    TaskChangelog,
    TaskStatusEdit,
    ApiVersionView,
    StoryIssueList,
    TaskProjectEdit,
    TaskVersionEdit,
    WorkflowRelease,
    TaskPriorityEdit,
    WorkflowDataList,
    TaskDeleteConfirm
  },
  data() {
    return {
      data: {
        objective: [],
        tags: []
      },
      loading: true,
      codeLoading: true,
      activeKey: ['1', '2', '3'],
      tabsCurrent: '1',
      taskId: parseInt(this.$route.query.taskId) || null,
      projectId: parseInt(this.$route.params.project_id) || null,
      issueList: [],
      tab_name_list: [],
      default_message: '',
      file_tab_list: [],
      file_name_list: [],
      tabItems: [
        { key: '1', label: '详细信息' },
        { key: '2', label: '关联流程' },
        { key: '3', label: '发布评审' },
        { key: '4', label: '关联缺陷' },
        { key: '5', label: '变更历史' },
        { key: '6', label: '相关代码' }
      ]
    };
  },
  computed: {
    link() {
      return record => {
        return this.$router.resolve({
          name: 'page:project:story:detail',
          query: { storyId: record.requirement.id }
        }).href;
      };
    },
    versionLink() {
      return record => {
        return this.$router.resolve({
          name: 'page:project:version',
          query: { version_id: record.version.id }
        }).href;
      };
    },
    isDeleted() {
      return this.data.is_deleted === 1;
    }
  },
  created() {
    if (this.taskId !== null) {
      this.getTaskDetail();
      this.getIssueList();
      this.getTagList();
    } else {
      this.$router.push({ name: 'page:project:task' });
    }
  },
  methods: {
    onTextCopy,
    aiScoreTrigger() {
      aiScore({ task_ids: [this.taskId], project_id: this.projectId })
        .then(() => {
          this.$message.success('AI评分触发成功');
          this.getTaskDetail();
        });
    },
    connectApi() {
      this.$nextTick(() => {
        this.$refs['apiConnectRef'].onOpen(this.data.agreements);
      });
    },

    /**
     * 获取任务详情
     */
    getTaskDetail() {
      taskDetail({ task_id: this.taskId, project_id: this.projectId })
        .then(res => {
          this.data = res.data.item;
          this.loading = false;
          this.$nextTick(() => {
            this.$refs['apiVersionViewRef'].init(this.data.agreements, this.taskId);
          });
        })
        .catch(() => {
          this.loading = false;
        });
    },

    /**
     * 编辑任务
     */
    onEdit() {
      this.$nextTick(() => {
        this.$refs['taskEditRef'].onOpen(this.taskId, this.data);
      });
    },

    /**
     * 删除任务
     */
    onDelete() {
      this.$nextTick(() => {
        this.$refs.taskDeleteRef.onOpen(this.taskId, this.projectId);
      });
    },

    /**
     * 点击移动项目
     */
    onMoveProject() {
      this.$nextTick(() => {
        this.$refs.taskProjectEditRef.onOpen({ id: this.taskId, value: this.projectId });
      });
    },

    /**
     * 项目移动成功回调事件，跳转至缺陷详情页
     * @param {*} project_id
     */
    onMoveOk(project_id) {
      this.$router
        .push({
          name: 'page:project:task:detail',
          params: { project_id: project_id },
          query: { taskId: this.taskId }
        })
        .then(() => {
          this.$router.go(0);
        });
    },

    /**
     * 提审任务
     */
    onCreateWorkflow() {
      const self = this;
      workflowFastCreate({
        record: {
          ...this.data,
          type: 'task',
          template_id: 13
        }
      }).then(res => {
        if (res.data.message === 'success') {
          self.$message.success('创建成功,正在跳转流程详情页～');
          self.$router.push({
            name: 'page:project:workflow:detail',
            query: { workflowId: res.data.workflow_id }
          });
        } else {
          if (res.data.message) {
            self.$message.warning(res.data.message);
          }
        }
      });
    },

    getTagList() {
      tagList({ group_code: 'task_req_tag' }).then(res => {
        this.tagData = res.data.items;
      });
    },

    /**
     * 点击修改迭代
     */
    onRelationVersion() {
      this.$nextTick(() => {
        this.$refs.taskVersionRef.onOpen(this.data);
      });
    },

    onAddComment() {
      if ('3'.includes(this.activeKey) === false) {
        this.activeKey.push('3');
      }
      this.$refs['comment'].onAddComment();
    },

    combineArrayWithCount(arr) {
      const countMap = {};
      return arr.map(item => {
        countMap[item] = (countMap[item] || 0) + 1;
        return `${item}第${countMap[item]}次提交`;
      });
    },

    /**
     * 获取缺陷列表
     */
    getIssueList() {
      issueList({ task_id: this.taskId, project_id: this.projectId }).then(res => {
        this.issueList = res.data.items;
      });
    },
    filteredTags(tags) {
      const uniqueTags = tags.filter((tag, index) => {
        return tags.findIndex(t => t.id === tag.id) === index;
      });

      const tagCounts = tags.reduce((counts, tag) => {
        counts[tag.id] = (counts[tag.id] || 0) + 1;
        return counts;
      }, {});

      return uniqueTags.map(tag => {
        const count = tagCounts[tag.id];
        const processedTag = { ...tag }; // 创建一个新对象来保存处理后的标签
        if (count > 1) {
          processedTag.name = processedTag.name + count + '次';
        }
        return processedTag;
      });
    },
    handleTabsChange(activeKey) {
      const item = this.tabItems.find(item => item.key === activeKey);
      this.utils.trackClick({ name: '切换卡片', label: item ? item.label : activeKey });
      if (this.tabsCurrent === '5') {
        this.$nextTick(() => {
          this.$refs['changelog'].getTaskChangelog();
        });
      }
    },

    /**
     * 回调
     * @param {*} type
     */
    close() {
      this.getTaskDetail();
      this.onCancel();
    },

    onCancel() {
      // 更新评论组件Key，否则会导致无法监听到键盘事件
      this.$nextTick(() => {
        this.$refs.commentRef.setKey();
      });
    }
  }
};
</script>

<style lang="less" scoped>
/deep/.ant-card-body {
  padding: 16px;

  .ant-page-header {
    padding: 0;
  }

  .ant-tabs-nav .ant-tabs-tab {
    margin: 0 8px 0 0;
  }
}

.issue-panel {
  border-radius: 4px;
  border: 0;
  background: #fff;
  overflow: hidden;
}

/deep/.ql-container.ql-snow {
  border: none;
  background: #fafafa;
  padding: 16px;
}
/deep/.ant-page-header-heading-extra {
  display: flex;
  align-items: center;
}
</style>
